import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CombinedReducers } from 'app_events/index';
import CourseActivityService from 'app_services/campus/academic/courseActivityService';
import moment from 'moment';
import { moodleAuthFormRequest } from 'app_events/auth/actions';
import { ScreenNotificationsUtil } from 'app_utils/screenNotification';
import { useTranslation } from 'react-i18next';
import DataTableComponent from 'app_components/commons/dataTable';
import LoadingWrapper from 'app_components/commons/LoadingWrapper/LoadingWrapper';
import StudentDashboardCustomTable from '../StudentDashboardCustomTable/StudentDashboardCustomTable';
import TableItemsPaginator from 'app_components/modules/TableItemsPaginator/TableItemsPaginator';
import { STUDENT_DASHBOARD_TAB } from '../../StudentDashboardPage';
import LoginMoodle from 'app_components/modules/LoginMoodle/LoginMoodle';
import ButtonComponent from 'app_components/commons/Button/ButtonComponent';
import { external_api } from 'app_config/globals';
import useWindowWidth from 'app_hooks/useWindowWidth';
import AccordionTable from 'app_components/modules/AccordionTable/AccordionTable';
import TextComponent from 'app_components/commons/TextComponent/TextComponent';
import './StudentDashboardActivitiesTable.scss'

interface IStudentDashboardActivitiesTableProps {
  tab?: STUDENT_DASHBOARD_TAB
  id?: string
}

const REQUEST_DATE_FORMAT = 'YYYY-MM-DD'
const DATE_FORMAT = 'DD/MM/YYYY'
const DATE_WITH_HOUR_FORMAT = 'DD/MM/YYYY HH:mm'
const ITEMS_PER_PAGE = 10
const MOBILE_BREAKPOINT = 800

const StudentDashboardActivitiesTable: FC<IStudentDashboardActivitiesTableProps> = ({ tab, id }) => {
  const { courseSelected } = useSelector((state: CombinedReducers) => state.courses)
  const courseActivityService = useMemo(() => new CourseActivityService(), [])
  const activityStatus: any = useMemo(() =>
    courseActivityService.getActivityTags()
      .reduce((accum, status) => ({ ...accum, [status.key.toString()]: status }), {}),
    [courseActivityService]
  )
  const screenNotifications = useMemo(() => new ScreenNotificationsUtil(), [])
  const auth = useSelector((state: CombinedReducers) => state.auth)
  const moodleAuth = useMemo(() => auth?.moodleAuth, [auth])
  const [activities, setActivities] = useState<unknown[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { windowWidth } = useWindowWidth()

  const handleJoinMeeting = useCallback((url: string) => {
    const authStatus = !!moodleAuth
    if (!authStatus) {
      dispatch(moodleAuthFormRequest(true))
      screenNotifications.fire({
        'message': 'Iniciando sesión en moodle',
      }, {
        'type': 'warning',
        'showCancelButton': false,
        'showConfirmButton': false,
        'allowOutsideClick': false,
        'allowEscapeKey': false,
        timer: 3000,
      })?.then(() => {
        window.location.href = url
      })
    } else {
      window.location.href = url
    }
  }, [dispatch, screenNotifications, moodleAuth])

  const joinMeetingButton = useCallback((url: string | null) => {
    if (!url) return;
    return (
      <button className='join-meeting-button' onClick={() => handleJoinMeeting(url)}>
        Join
      </button>
    )
  }, [handleJoinMeeting])

  const extractUrlFromTag = useCallback((tag: string) => {
    const regex = new RegExp(/href="(.*?)"/, 'g')
    if (!regex) return null;
    const regexExec = regex.exec(tag)
    if (!regexExec || !regexExec[1]) return null;
    return regexExec[1]
  }, [])

  const getGoToExamButton = (id: string) => {
    return (
      <LoginMoodle redirectTo={`${external_api?.moodle?.url}/mod/quiz/view.php?id=${id}`} targetBlank>
        <ButtonComponent
          variant="secondary"
          width="100%"
          textTransform='initial'
          borderRadius="20px"
          padding="2px 10px"
          type="button"
        >
          Ir al examen
        </ButtonComponent>
      </LoginMoodle>
    )
  }

  const getActivities = useCallback(async () => {
    if (!courseSelected?._id) {
      setActivities([])
      return
    }
    setIsLoading(true)
    const startOfMonth = `${moment().startOf('month').format(REQUEST_DATE_FORMAT)}T00:00:00Z`;
    const endOfMonth = `${moment().endOf('month').format(REQUEST_DATE_FORMAT)}T23:59:59Z`;

    const dataResponse = await courseActivityService.fetchActivityCalendar({
      courseID: courseSelected._id,
      courseSchedulingId: courseSelected.courseScheduling,
      timeStart: startOfMonth,
      timeEnd: endOfMonth,
      events: ["assign", "quiz", "forum"]
    })
    if (dataResponse && dataResponse?.events?.length) {
      const newEvents = dataResponse.events.map((event, idx) => {
        let startDate = event.timestart ? moment(event.timestart).format(DATE_FORMAT) : null;
        let endDate = event.timefinish ? moment(event.timefinish).format(DATE_FORMAT) : null;
        let component = undefined;

        if (event?.eventtype === 'webex') {
          startDate = event.timestart ? moment(event.timestart).format(DATE_WITH_HOUR_FORMAT) : null;
          endDate = event.timefinish ? moment(event.timefinish).format(DATE_WITH_HOUR_FORMAT) : null;
          if (endDate) {
            const today = moment()
            const isActive = moment(event.timefinish).isSameOrAfter(today)
            if (isActive) {
              if (event?.url) {
                component = joinMeetingButton(
                  event.url
                )
              } else if (event?.description) {
                component = joinMeetingButton(
                  extractUrlFromTag(event?.description)
                )
              }
            }
          }
        }

        return {
          _id: event.id,
          position: `${idx + 1}`,
          activity: event.name,
          module: event.modulename,
          sectionName: event.sectionName,
          start_date: startDate,
          limit_date: endDate,
          delivery_date: (event.timecompleted != null) ? moment(event.timecompleted).format(DATE_FORMAT) : null,
          circle_component: (activityStatus && activityStatus[event.status]) ? activityStatus[event.status].color : null,
          component_render_actions: component,
          qualification: event.qualification ? `${event.qualification}/100` : '',
          component_render_join: event.activityType === 'quiz' ? getGoToExamButton(String(event.id)) : null,
          activityType: event.activityType
            ? t(`modules.activity_details.activity_type.${event.activityType}`):
            '',
        }
      })
      setActivities([...newEvents])
    } else {
      setActivities([])
    }
    setIsLoading(false)
  }, [
    courseSelected,
    courseActivityService,
    joinMeetingButton,
    activityStatus,
    extractUrlFromTag,
    t,
  ])

  useEffect(() => {
    getActivities()
  }, [getActivities])

  const headerKeys = useMemo(() => [
    `${t('modules.activity_details.headers.course_name')}`,
    `${t('modules.activity_details.headers.activity')}`,
    ...tab !== STUDENT_DASHBOARD_TAB.HISTORY ? [`${t('modules.activity_details.headers.join_exam')}`] : [],
    `${t('modules.activity_details.headers.start_date')}`,
    `${t('modules.activity_details.headers.limit_date')}`,
    `${t('modules.activity_details.headers.made_in')}`,
    `${t('modules.activity_details.headers.status')}`,
    `${t('modules.activity_details.headers.qualification')}`,
  ], [t, tab])

  const accordionNamesList = useMemo(() => [
    `${t('modules.activity_details.headers.course_name')}`,
    `${t('modules.activity_details.headers.activity')}`,
    ...tab !== STUDENT_DASHBOARD_TAB.HISTORY ? [`${t('modules.activity_details.headers.join_exam')}`] : [],
    `${t('modules.activity_details.headers.start_date')}`,
    `${t('modules.activity_details.headers.limit_date')}`,
    `${t('modules.activity_details.headers.made_in')}`,
    `${t('modules.activity_details.headers.qualification')}`,
  ], [t, tab])

  const keys = useMemo(() => [
    'sectionName',
    'activityType',
    ...tab !== STUDENT_DASHBOARD_TAB.HISTORY ? ['component_render_join'] : [],
    'start_date',
    'limit_date',
    'delivery_date',
    'circle_component',
    'qualification',
  ], [tab])

  const accordionDataKeys = useMemo(() => [
    'sectionName',
    'activityType',
    ...tab !== STUDENT_DASHBOARD_TAB.HISTORY ? ['component_render_join'] : [],
    'start_date',
    'limit_date',
    'delivery_date',
    'qualification',
  ], [tab])

  const tooltipKeys = useMemo(() => ['sectionName'], [])

  return (
    <div id={id} className="student-dashboard-activities-table--layout">
      <LoadingWrapper loading={isLoading} height="80px">
        <>
          <TableItemsPaginator items={activities} perPage={ITEMS_PER_PAGE}>
            {(itemsToRender) => (
              windowWidth && windowWidth > MOBILE_BREAKPOINT ? (
                <StudentDashboardCustomTable>
                  <DataTableComponent
                    data={itemsToRender}
                    textAlign='left'
                    headerKeys={headerKeys}
                    keys={keys}
                    tooltipKeys={tooltipKeys}
                    detailsKey={[]}
                    onModal={null}
                  />
                </StudentDashboardCustomTable>
              ) : (
                <AccordionTable
                  id={`${id}-accordion-table`}
                  items={itemsToRender}
                  namesList={accordionNamesList}
                  dataKeys={accordionDataKeys}
                  renderHeader={(item) => (
                    <TextComponent fontSize="14px" elipsis>
                      {item.sectionName}
                    </TextComponent>
                  )}
                />
              )
            )}
          </TableItemsPaginator>
          <div className='student-dashboard-activities-table--status'>
            {courseActivityService.getActivityTags()?.map((tag) => (
              <div key={tag.key} className='student-dashboard-activities-table--status-card'>
                <div
                  className='student-dashboard-activities-table--status-circle'
                  style={{ backgroundColor: tag.color }}
                />
                <TextComponent>{tag.label}</TextComponent>
              </div>
            ))}
          </div>
        </>
      </LoadingWrapper>
    </div>
  )
}

export default StudentDashboardActivitiesTable
